import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { NAVIGATION_KEYS, NS } from '../../../i18n/constants/keys';
import { Permission } from '../../../utils/helpers/permissions';
import { RootState } from '../../../utils/redux_store/store';
import {
  BrainIcon,
  BriefcaseBusinessIcon,
  ChartPieIcon,
  ChevronLeftIcon,
  CircleHelpIcon,
  ClipboardCheckIcon,
  CompassIcon,
  DatabaseBackupIcon,
  FilePenLineIcon,
  FileTextIcon,
  HeartHandshakeIcon,
  IdCardIcon,
  LayoutGridIcon,
  LockKeyholeIcon,
  SearchIcon,
  SettingsIcon,
  SlidersHorizontalIcon,
  SparklesIcon,
  UserCheckIcon,
  UserIcon,
  UserRoundCheckIcon,
  UserRoundPlusIcon,
  UsersRoundIcon,
  XIcon,
} from '../../../icons/animated';
import { AppIcon } from './AppIcon';

export interface AppsFolderProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AppDefinition {
  id: string;
  name: string;
  description?: string;
  icon: React.ReactNode;
  gradient: string;
  to?: string;
  isFolder?: boolean;
  subItems?: AppDefinition[];
  requiredPermissions?: Permission[];
  matchAnyPermission?: boolean;
  hideIf?: boolean;
}

export const AppsFolder: React.FC<AppsFolderProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(NS.navigation);
  const history = useHistory();
  const location = useLocation();
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const userPermissions = useSelector(
    (state: RootState) => state.permissions.permissions,
  );
  const role = localStorage.getItem('role');

  const hasPermission = (
    requiredPermissions?: Permission[],
    matchAny = false,
  ): boolean => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    if (matchAny) {
      return requiredPermissions.some((p) => userPermissions.includes(p));
    }
    return requiredPermissions.every((p) => userPermissions.includes(p));
  };

  const isLicenseActive = (feature?: {
    startDate: number | string;
    endDate: number | string;
  }): boolean => {
    if (!feature) return false;
    return Number(feature.endDate) * 1000 > Date.now();
  };

  // Close on Escape key and body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeFolderId) {
          setActiveFolderId(null);
        } else {
          onClose();
        }
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      setSearchQuery('');
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, activeFolderId, onClose]);

  // App definitions
  const allApps: AppDefinition[] = useMemo(() => {
    return [
      {
        id: 'dashboard',
        name: t(NAVIGATION_KEYS.sidebar.dashboard) || 'Dashboard',
        description: 'Participant & overview hub',
        icon: <LayoutGridIcon className="text-white" size={28} />,
        gradient: 'from-emerald-500 to-teal-700',
        to: '/home',
      },
      {
        id: 'hierTalent',
        name: t(NAVIGATION_KEYS.sidebar.hierTalent) || 'HierTalent',
        description: 'Job openings & candidate pipelines',
        icon: <BriefcaseBusinessIcon className="text-white" size={28} />,
        gradient: 'from-blue-600 to-indigo-700',
        to: '/job-openings-list',
        requiredPermissions: [
          Permission.ASSESSMENTS_CREATE,
          Permission.QUESTIONS_VIEW,
        ],
        matchAnyPermission: true,
      },
      {
        id: 'questions_folder',
        name: t(NAVIGATION_KEYS.sidebar.questions) || 'Questions',
        description: 'Question bank, prescreen & CCA',
        icon: <CircleHelpIcon className="text-white" size={28} />,
        gradient: 'from-purple-600 via-pink-600 to-rose-600',
        isFolder: true,
        requiredPermissions: [Permission.QUESTIONS_VIEW],
        subItems: [
          {
            id: 'question_bank',
            name: t(NAVIGATION_KEYS.sidebar.questionBank) || 'Question Bank',
            description: 'Central repository of assessment questions',
            icon: <DatabaseBackupIcon className="text-white" size={28} />,
            gradient: 'from-purple-500 to-indigo-600',
            to: '/question-bank',
            requiredPermissions: [Permission.QUESTIONS_VIEW],
          },
          {
            id: 'pre_screen',
            name: t(NAVIGATION_KEYS.sidebar.preScreenQuestions) || 'Pre-Screen Questions',
            description: 'Candidate screening questions',
            icon: <ClipboardCheckIcon className="text-white" size={28} />,
            gradient: 'from-fuchsia-500 to-pink-600',
            to: '/pre-screen-bank',
            requiredPermissions: [Permission.QUESTIONS_VIEW],
          },
          {
            id: 'content_manager',
            name: t(NAVIGATION_KEYS.sidebar.contentManager) || 'Content Manager',
            description: 'Custom question and job content manager',
            icon: <FilePenLineIcon className="text-white" size={28} />,
            gradient: 'from-pink-500 to-rose-600',
            to: '/content-manager',
            requiredPermissions: [Permission.JOB_VIEW],
          },
          {
            id: 'auto_generate',
            name: t(NAVIGATION_KEYS.sidebar.autoGenerateQuestions) || 'Auto Generate Questions',
            description: 'Generate questions automatically from documents',
            icon: <SparklesIcon className="text-white" size={28} />,
            gradient: 'from-rose-500 to-orange-500',
            to: '/upload-file',
            requiredPermissions: [Permission.JOB_CREATE],
          },
          {
            id: 'cca_questions',
            name: t(NAVIGATION_KEYS.sidebar.ccaQuestions) || 'CCA Questions',
            description: 'Cognitive & behavioural assessments',
            icon: <BrainIcon className="text-white" size={28} />,
            gradient: 'from-violet-600 to-purple-800',
            to: '/cca-questions',
            requiredPermissions: [Permission.QUESTIONS_VIEW],
            hideIf: !isLicenseActive(subscriptions?.hierTalentCca),
          },
        ],
      },
      {
        id: 'reporting',
        name: t(NAVIGATION_KEYS.sidebar.reporting) || 'Reporting',
        description: 'Analytics, funnels and PDF exports',
        icon: <ChartPieIcon className="text-white" size={28} />,
        gradient: 'from-amber-500 to-orange-600',
        to: '/reporting',
      },
      {
        id: 'job_postings',
        name: t(NAVIGATION_KEYS.sidebar.jobPostings) || 'Job Postings',
        description: 'Active public job board & listings',
        icon: <FileTextIcon className="text-white" size={28} />,
        gradient: 'from-cyan-500 to-blue-600',
        to: '/jobs',
      },
      {
        id: 'candibank',
        name: t(NAVIGATION_KEYS.sidebar.candibankExplorer) || 'Candibank Explorer',
        description: 'Explore curated candidate talent bank',
        icon: <UsersRoundIcon className="text-white" size={28} />,
        gradient: 'from-teal-600 to-emerald-700',
        to: '/candibank-explorer',
        hideIf: !subscriptions?.hierTalentCandiBank || role === 'Candidate',
      },
      {
        id: 'settings_folder',
        name: 'System Settings',
        description: 'Access management, organization & frameworks',
        icon: <SettingsIcon className="text-white" size={28} />,
        gradient: 'from-slate-700 via-slate-800 to-zinc-900',
        isFolder: true,
        hideIf: role === 'Candidate',
        subItems: [
          {
            id: 'access_management',
            name: 'Users & Roles',
            description: 'Manage users, roles and permission access',
            icon: <UserRoundPlusIcon className="text-white" size={28} />,
            gradient: 'from-blue-600 to-indigo-700',
            to: '/access-management',
            requiredPermissions: [Permission.USERS_VIEW],
          },
          {
            id: 'assessment_framework',
            name: 'Assessment Framework',
            description: 'Configure assessment frameworks & traits',
            icon: <CompassIcon className="text-white" size={28} />,
            gradient: 'from-purple-600 to-indigo-700',
            to: '/assessment-framework',
            requiredPermissions: [Permission.ASSESSMENTS_VIEW],
          },
          {
            id: 'organization_settings',
            name: 'Organization Settings',
            description: 'Organization units, branding and preferences',
            icon: <SlidersHorizontalIcon className="text-white" size={28} />,
            gradient: 'from-teal-600 to-emerald-700',
            to: '/organization-settings',
            requiredPermissions: [Permission.ORGUNIT_VIEW],
          },
          {
            id: 'employee_management',
            name: 'Employee Management',
            description: 'Manage staff and employee directory',
            icon: <IdCardIcon className="text-white" size={28} />,
            gradient: 'from-emerald-600 to-teal-800',
            to: '/employee-management',
          },
          {
            id: 'candidate_management',
            name: 'Candidate Management',
            description: 'Talent candidates and application pipelines',
            icon: <UserCheckIcon className="text-white" size={28} />,
            gradient: 'from-cyan-600 to-blue-700',
            to: '/candidate-management',
          },
          {
            id: 'partner_management',
            name: 'Partner Management',
            description: 'External partners and integrations',
            icon: <HeartHandshakeIcon className="text-white" size={28} />,
            gradient: 'from-amber-600 to-orange-700',
            to: '/partner-management',
          },
        ],
      },
      {
        id: 'profile_folder',
        name: 'Profile & Account',
        description: 'User profile, password & security',
        icon: <UserRoundCheckIcon className="text-white" size={28} />,
        gradient: 'from-teal-600 via-emerald-700 to-green-800',
        isFolder: true,
        subItems: [
          {
            id: 'user_profile',
            name: 'My Profile',
            description: 'Personal details and account preferences',
            icon: <UserIcon className="text-white" size={28} />,
            gradient: 'from-emerald-600 to-teal-700',
            to: '/user-profile',
          },
          {
            id: 'change_password',
            name: 'Change Password',
            description: 'Update password and security credentials',
            icon: <LockKeyholeIcon className="text-white" size={28} />,
            gradient: 'from-slate-700 to-slate-900',
            to: '/change-password',
          },
        ],
      },
    ];
  }, [t, subscriptions, role, userPermissions]);

  // Filter accessible apps
  const accessibleApps = useMemo(() => {
    return allApps
      .filter((app) => !app.hideIf && hasPermission(app.requiredPermissions, app.matchAnyPermission))
      .map((app) => {
        if (!app.subItems) return app;
        return {
          ...app,
          subItems: app.subItems.filter(
            (sub) => !sub.hideIf && hasPermission(sub.requiredPermissions, sub.matchAnyPermission),
          ),
        };
      });
  }, [allApps, userPermissions]);

  // Auto-detect and open into contextual folder if on a sub-route (e.g. Questions)
  useEffect(() => {
    if (isOpen) {
      const matchedFolder = accessibleApps.find(
        (app) =>
          app.isFolder &&
          app.subItems?.some(
            (sub) =>
              sub.to === location.pathname ||
              (sub.to && sub.to !== '/' && location.pathname.startsWith(sub.to)),
          ),
      );
      if (matchedFolder) {
        setActiveFolderId(matchedFolder.id);
      } else {
        setActiveFolderId(null);
      }
    }
  }, [isOpen, location.pathname, accessibleApps]);

  // Active folder definition
  const activeFolder = useMemo(() => {
    if (!activeFolderId) return null;
    return accessibleApps.find((a) => a.id === activeFolderId && a.isFolder);
  }, [activeFolderId, accessibleApps]);

  // Filtered list based on search
  const displayedApps = useMemo(() => {
    if (activeFolder) {
      return activeFolder.subItems || [];
    }
    if (!searchQuery.trim()) {
      return accessibleApps;
    }
    const q = searchQuery.toLowerCase();
    const matches: AppDefinition[] = [];

    accessibleApps.forEach((app) => {
      if (app.name.toLowerCase().includes(q) || (app.description && app.description.toLowerCase().includes(q))) {
        matches.push(app);
      }
      if (app.subItems) {
        app.subItems.forEach((sub) => {
          if (sub.name.toLowerCase().includes(q) || (sub.description && sub.description.toLowerCase().includes(q))) {
            if (!matches.some((m) => m.id === sub.id)) {
              matches.push(sub);
            }
          }
        });
      }
    });

    return matches;
  }, [accessibleApps, activeFolder, searchQuery]);

  const handleAppClick = (app: AppDefinition) => {
    if (app.isFolder) {
      setActiveFolderId(app.id);
    } else if (app.to) {
      history.push(app.to);
      onClose();
    }
  };

  return (
    <MotionConfig transition={{ type: 'spring', stiffness: 300, damping: 30, mass: 1 }}>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 sm:p-6 select-none">
            {/* Reduced Light Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              className="fixed inset-0 bg-slate-900/20 backdrop-blur-[2px]"
            />

            {/* iOS App Folder Modal Container - Light Theme with Crisp Spring Exit */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{
                opacity: 0,
                scale: 0.94,
                y: 8,
                transition: { duration: 0.15, ease: 'easeOut' },
              }}
              transition={{
                type: 'spring',
                stiffness: 380,
                damping: 28,
                mass: 0.8,
              }}
              className="relative w-full max-w-2xl bg-white/95 border border-slate-200/90 rounded-3xl p-6 sm:p-8 text-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.12)] backdrop-blur-xl overflow-hidden z-10"
              style={{
                boxShadow:
                  '0 25px 60px -15px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
              }}
            >
            {/* Header / Search / Title */}
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {activeFolder ? (
                    <button
                      type="button"
                      onClick={() => setActiveFolderId(null)}
                      className="group flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-all active:scale-95 border border-slate-200/60"
                    >
                      <ChevronLeftIcon size={16} className="text-slate-600 group-hover:text-slate-900" />
                      Back
                    </button>
                  ) : (
                    <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center">
                      <LayoutGridIcon size={16} className="text-emerald-600" />
                    </div>
                  )}

                  <div>
                    <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                      {activeFolder ? activeFolder.name : 'HiAcuity Applications'}
                    </h2>
                    <p className="text-xs text-slate-500">
                      {activeFolder
                        ? activeFolder.description || 'Application sub-items'
                        : 'Quick launch apps and workflows'}
                    </p>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  onClick={onClose}
                  className="group w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center transition-colors"
                  aria-label="Close apps dock"
                >
                  <XIcon size={16} className="text-slate-500 group-hover:text-slate-800" />
                </button>
              </div>

              {/* Search Bar */}
              {!activeFolder && (
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <SearchIcon size={16} className="text-slate-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search applications..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-50 hover:bg-slate-100/60 focus:bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="group absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                      aria-label="Clear search"
                    >
                      <XIcon size={16} className="text-slate-400 group-hover:text-slate-600" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Apps Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 sm:gap-6 max-h-[50vh] overflow-y-auto p-1 custom-scrollbar">
              {displayedApps.length > 0 ? (
                displayedApps.map((app) => {
                  const isActive =
                    app.to === location.pathname ||
                    (app.subItems && app.subItems.some((s) => s.to === location.pathname));

                  return (
                    <AppIcon
                      key={app.id}
                      id={app.id}
                      name={app.name}
                      description={app.description}
                      icon={app.icon}
                      gradient={app.gradient}
                      isActive={isActive}
                      isFolder={app.isFolder}
                      itemCount={app.subItems?.length}
                      onClick={() => handleAppClick(app)}
                    />
                  );
                })
              ) : (
                <div className="col-span-full py-12 text-center text-slate-400 text-sm">
                  No applications found matching &quot;{searchQuery}&quot;
                </div>
              )}
            </div>

            {/* Bottom Keyboard Hint */}
            <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px] text-slate-700 font-semibold shadow-xs">
                    Ctrl
                  </kbd>
                  <span>+</span>
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px] text-slate-700 font-semibold shadow-xs">
                    Space
                  </kbd>
                  <span className="text-slate-400">or</span>
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px] text-slate-700 font-semibold shadow-xs">
                    ⌘
                  </kbd>
                  <span>+</span>
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px] text-slate-700 font-semibold shadow-xs">
                    Space
                  </kbd>
                  <span className="text-slate-400 ml-1">to toggle</span>
                </span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px] text-slate-600">
                    ESC
                  </kbd>
                  to close
                </span>
              </div>
              <span className="text-slate-400">HiAcuity Workspace</span>
            </div>
          </motion.div>
        </div>
      )}
      </AnimatePresence>
    </MotionConfig>
  );
};
